<script setup lang="ts">
import { computed } from 'vue';
import { UiText, UiButton, UiLink, UiSquareButton, UiTooltip, getUrgencyLabel } from '@shopexperts/shared';
import type { DraftLeadAdminResponse } from '../types';
import { formatTimezoneWithOffset } from '../utils/timezone';

interface DraftLeadCardProps {
    draftLead: DraftLeadAdminResponse;
}

const props = defineProps<DraftLeadCardProps>();

const emit = defineEmits<{
    (e: 'edit', draftLead: DraftLeadAdminResponse): void;
    (e: 'submit', draftLead: DraftLeadAdminResponse): void;
    (e: 'delete', draftLead: DraftLeadAdminResponse): void;
}>();

// Format date
const formattedCreatedDate = computed(() => {
    const date = new Date(props.draftLead.created_at);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
});

const formattedUpdatedDate = computed(() => {
    const date = new Date(props.draftLead.updated_at);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
});

const formattedSubmittedDate = computed(() => {
    if (!props.draftLead.submitted_at) return null;
    const date = new Date(props.draftLead.submitted_at);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
});

// Status badge color
const statusColor = computed(() => {
    return props.draftLead.is_submitted 
        ? 'bg-green-100 text-green-800' 
        : 'bg-yellow-100 text-yellow-800';
});

const statusLabel = computed(() => {
    return props.draftLead.is_submitted ? 'Submitted' : 'Pending';
});

// Urgency badge color
const urgencyColor = computed(() => {
    if (!props.draftLead.urgency) return 'bg-gray-100 text-gray-600';
    switch (props.draftLead.urgency) {
        case 'urgent':
        case 'as_soon_as_possible':
            return 'bg-red-100 text-red-800';
        case 'within_the_next_week':
            return 'bg-orange-100 text-orange-800';
        case 'within_the_next_month':
            return 'bg-yellow-100 text-yellow-800';
        case 'standard':
        case 'open_to_expert_opinion':
        default:
            return 'bg-blue-100 text-blue-800';
    }
});

// Urgency display label
const urgencyLabel = computed(() => {
    if (!props.draftLead.urgency) return null;
    return getUrgencyLabel(props.draftLead.urgency);
});

// Matching strategy badge color
const matchingStrategyColor = computed(() => {
    if (!props.draftLead.matching_strategy) return 'bg-gray-100 text-gray-600';
    switch (props.draftLead.matching_strategy) {
        case 'selected':
            return 'bg-purple-100 text-purple-800';
        case 'selected-auto':
            return 'bg-indigo-100 text-indigo-800';
        case 'auto':
            return 'bg-teal-100 text-teal-800';
        default:
            return 'bg-gray-100 text-gray-600';
    }
});

// Completion status
const completionPercentage = computed(() => {
    const totalFields = 8; // email, first_name, last_name, project_title, store_url, urgency, matching_strategy, selected_expert_id
    const missingCount = props.draftLead.missing_fields.length;
    return Math.round(((totalFields - missingCount) / totalFields) * 100);
});

// Can submit (must be complete and not already submitted)
const canSubmit = computed(() => {
    return props.draftLead.is_complete && !props.draftLead.is_submitted;
});

// Can edit (only if not submitted)
const canEdit = computed(() => {
    return !props.draftLead.is_submitted;
});

// Full name
const fullName = computed(() => {
    const parts = [props.draftLead.first_name, props.draftLead.last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
});

const budgetLabel = computed(() => props.draftLead.project_value_range?.label || 'Flexible');
const timezoneLabel = computed(() => formatTimezoneWithOffset(props.draftLead.timezone));

function onEditClick() {
    emit('edit', props.draftLead);
}

function onSubmitClick() {
    emit('submit', props.draftLead);
}

function onDeleteClick() {
    emit('delete', props.draftLead);
}
</script>

<template>
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
        <div class="flex gap-6 items-stretch">
            <!-- Left Column: Draft Details -->
            <div class="flex-1 flex flex-col min-w-0">
                <!-- ID & Project Title -->
                <div class="mb-3">
                    <div class="flex items-center gap-2">
                        <UiText as="h5" variant="h5" class="font-semibold mb-1 text-se-gray-500">
                            #{{ draftLead.id }}
                        </UiText>
                        <UiText as="h4" variant="p" class="font-semibold mb-1">
                            {{ draftLead.project_title || 'Untitled Draft' }}
                        </UiText>
                    </div>
                    
                    <!-- Store URL -->
                    <UiLink 
                        v-if="draftLead.store_url" 
                        :href="draftLead.store_url" 
                        external="with-arrow" 
                        variant="p-small"
                        :label="draftLead.store_url" 
                    />
                    <UiText v-else as="span" variant="p-small" class="text-gray-400 italic">
                        No store URL
                    </UiText>
                </div>

                <!-- Project Description -->
                <UiText 
                    v-if="draftLead.project_description" 
                    as="p" 
                    variant="p-small" 
                    class="text-gray-600 mb-3"
                    :text="draftLead.project_description" 
                    :truncate="3" 
                    :show-more-button="true" 
                />

                <div class="flex-1"></div>

                <!-- Metadata Row -->
                <div class="grid grid-cols-2 md:grid-cols-4 items-start text-xs text-gray-600 gap-2 justify-space-between w-full">
                    <div>
                        <UiText as="caption" variant="caption-normal" class="text-gray-600" text="Created: " />
                        <UiText as="caption" variant="caption-normal" class="font-semibold" :text="formattedCreatedDate" />
                    </div>
                    <div>
                        <UiText as="caption" variant="caption-normal" class="text-gray-600" text="Updated: " />
                        <UiText as="caption" variant="caption-normal" class="font-semibold" :text="formattedUpdatedDate" />
                    </div>
                    <div>
                        <UiText as="caption" variant="caption-normal" class="text-gray-600" text="Budget: " />
                        <UiText as="caption" variant="caption-normal" class="font-semibold" :text="budgetLabel" />
                    </div>
                    <div>
                        <UiText as="caption" variant="caption-normal" class="text-gray-600" text="Timezone: " />
                        <UiText as="caption" variant="caption-normal" class="font-semibold" :text="timezoneLabel" />
                    </div>
                    <div v-if="draftLead.brand_name" class="md:col-span-2">
                        <UiText as="caption" variant="caption-normal" class="text-gray-600" text="Brand: " />
                        <UiText as="caption" variant="caption-normal" class="font-semibold" :text="draftLead.brand_name" />
                    </div>
                </div>
            </div>

            <!-- Right Column: Status & Actions -->
            <div class="w-80 flex-shrink-0 flex flex-col gap-2">
                <!-- Status Badges -->
                <div class="flex gap-1 justify-end flex-wrap">
                    <span :class="statusColor" class="px-2 py-0.5 rounded text-xs font-medium">
                        {{ statusLabel }}
                    </span>
                    <span v-if="draftLead.urgency" :class="urgencyColor" class="px-2 py-0.5 rounded text-xs font-medium">
                        {{ urgencyLabel }}
                    </span>
                    <span v-if="draftLead.matching_strategy" :class="matchingStrategyColor" class="px-2 py-0.5 rounded text-xs font-medium capitalize">
                        {{ draftLead.matching_strategy }}
                    </span>
                </div>

                <!-- Completion Progress -->
                <div class="mb-2 p-2 bg-gray-50 rounded-md">
                    <div class="flex items-center justify-between mb-1">
                        <UiText as="p" variant="p-small" class="text-gray-700 text-xs">
                            Completion
                        </UiText>
                        <UiText as="p" variant="p-small" class="font-bold text-xs">
                            {{ completionPercentage }}%
                        </UiText>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                            class="h-1.5 rounded-full transition-all"
                            :class="draftLead.is_complete ? 'bg-green-500' : 'bg-yellow-500'"
                            :style="{ width: `${completionPercentage}%` }"
                        ></div>
                    </div>
                    <div v-if="draftLead.missing_fields.length > 0 && !draftLead.is_submitted" class="mt-1">
                        <UiText as="p" variant="caption-normal" class="text-gray-500 text-xs">
                            Missing: {{ draftLead.missing_fields.join(', ').replace(/_/g, ' ') }}
                        </UiText>
                    </div>
                </div>

                <!-- Selected Expert -->
                <div v-if="draftLead.selected_expert" class="p-2 bg-blue-50 border border-blue-200 rounded-md">
                    <UiText as="p" variant="p-small" class="text-blue-800 text-xs mb-1">
                        Selected Expert
                    </UiText>
                    <UiText as="p" variant="p-small" class="font-semibold text-xs">
                        {{ draftLead.selected_expert.name }}
                    </UiText>
                </div>

                <!-- Selected Packaged Service -->
                <div v-if="draftLead.selected_packaged_service" class="p-2 bg-purple-50 border border-purple-200 rounded-md">
                    <UiText as="p" variant="p-small" class="text-purple-800 text-xs mb-1">
                        Packaged Service
                    </UiText>
                    <UiText as="p" variant="p-small" class="font-semibold text-xs">
                        {{ draftLead.selected_packaged_service.name }}
                    </UiText>
                </div>

                <!-- Submitted Lead Link -->
                <div v-if="draftLead.submitted_lead_id" class="p-2 bg-green-50 border border-green-200 rounded-md">
                    <UiText as="p" variant="p-small" class="text-green-800 text-xs mb-1">
                        Submitted as Lead #{{ draftLead.submitted_lead_id }}
                    </UiText>
                    <UiText as="p" variant="caption-normal" class="text-green-600 text-xs">
                        {{ formattedSubmittedDate }}
                    </UiText>
                </div>

                <!-- Action Buttons -->
                <div class="mt-auto">
                    <div class="flex items-center gap-2">
                        <!-- Edit Button -->
                        <UiTooltip v-if="canEdit" content="Edit draft">
                            <UiButton variant="secondary" label="Edit" icon="pencil" @click="onEditClick" />
                        </UiTooltip>

                        <!-- Submit Button -->
                        <UiTooltip 
                            v-if="!draftLead.is_submitted"
                            :content="canSubmit ? 'Submit draft lead' : 'Complete all required fields first'"
                        >
                            <UiButton 
                                variant="primary" 
                                label="Submit"
                                icon="arrow-send"
                                :disabled="!canSubmit"
                                @click="onSubmitClick"
                            />
                        </UiTooltip>

                        <div class="flex-1"></div>

                        <!-- Delete Button -->
                        <UiTooltip content="Delete draft">
                            <UiSquareButton icon="delete" variant="ghost" @click="onDeleteClick" />
                        </UiTooltip>
                    </div>
                </div>
            </div>
        </div>

        <!-- Divider & Client Details -->
        <div class="border-t border-gray-200 mt-4 pt-4">
            <div class="flex items-center gap-4 flex-wrap">
                <UiText as="p" variant="p-small" class="text-gray-500 text-xs">
                    Client:
                </UiText>
                <UiText v-if="fullName" as="p" variant="p-small" class="font-semibold text-sm">
                    {{ fullName }}
                </UiText>
                <UiLink 
                    :href="`mailto:${draftLead.email}`" 
                    variant="p-small"
                    :label="draftLead.email" 
                    class="text-blue-600 hover:underline"
                />
                <template v-if="draftLead.created_by_admin">
                    <span class="text-gray-300">|</span>
                    <UiText as="p" variant="p-small" class="text-gray-500 text-xs">
                        Created by: {{ draftLead.created_by_admin.name }}
                    </UiText>
                </template>
            </div>
        </div>
    </div>
</template>
